/**
 * WC 2026 · Customer-facing cover-design labels.
 *
 * Single source of truth for the "Album Type / Cover Design" dropdown
 * on the Add Album wizard AND for anywhere else on the collector-facing
 * app that displays a WC 2026 album variant name (profile card,
 * dashboard, album inventory header, museum, marketing preview).
 *
 * Rules (approved 2026-08):
 *   · Labels are pure display metadata. They never change the
 *     canonical checklist ID (`panini-wc-2026-canonical`), the 992
 *     checklist size, sticker codes, progress, missing lists,
 *     duplicate quantities, album IDs, or public URLs.
 *   · Existing catalog IDs (variant codes) are preserved verbatim.
 *     Nothing is duplicated in the catalog.
 *   · `Hardcover - United Edition` is a display-only rename of the
 *     Switzerland catalog record (WC26-CH). The internal record is
 *     unchanged.
 *   · `Hardcover - Mexico Green - 1 of 2026 - Special Edition` is a
 *     genuinely separate cover — a real catalog record must exist
 *     before it can be offered here. When the record is not yet in
 *     the catalog, the option is intentionally omitted (see the
 *     `MEXICO_GREEN_SPECIAL_EDITION_CODE` constant below).
 *   · `Other` is a savable option for collectors whose cover is not
 *     listed. It uses the Softcover catalog record as the safe
 *     fallback variant (still resolves to the same canonical
 *     checklist) and persists the customer-facing label via the
 *     `cover_label_override` album-instance field.
 */

// ────────────────────────────────────────────────────────────────
// Special sentinels
// ────────────────────────────────────────────────────────────────

/** Dropdown value used when the collector picks "Other". */
export const OTHER_OPTION_VALUE = "__wc2026_other__";

/** Customer-facing label saved on the album when "Other" is picked. */
export const OTHER_COVER_LABEL = "Other";

/**
 * Fallback variant code the "Other" option submits under. Softcover
 * is chosen because it is the most common cover and still resolves
 * to `panini-wc-2026-canonical` (992 positions).
 */
export const OTHER_FALLBACK_VARIANT_CODE = "WC26-US-SC";

/**
 * If a real Mexico-Green-Special-Edition catalog record ever ships,
 * set this to its variant code and the option will start appearing
 * automatically. As of the final pre-deployment iter (2026-08-03)
 * the catalog record `WC26-MX-GR-SE` was seeded, so the option is
 * now materialised at position 7 of the dropdown.
 */
export const MEXICO_GREEN_SPECIAL_EDITION_CODE = "WC26-MX-GR-SE";

// ────────────────────────────────────────────────────────────────
// Ordered dropdown labels
// ────────────────────────────────────────────────────────────────

/**
 * Approved dropdown order — each entry is either:
 *   { code: "WC26-XX", label: "…" }      → maps to a real catalog variant
 *   { special: OTHER_OPTION_VALUE }      → the "Other" sentinel
 *
 * The wizard filters this list against the actual variants returned
 * by `GET /api/catalog/releases/wc26/variants` and drops any entry
 * whose `code` is not present (see `buildWcCoverOptions()` below).
 * That keeps this file the single ordering authority while never
 * showing a broken row when the catalog changes.
 */
export const WC_2026_COVER_LABEL_ORDER = [
  { code: "WC26-US-SC", label: "Softcover - USA" },
  { code: "WC26-US-HC", label: "Hardcover - USA" },
  { code: "WC26-GOLD",  label: "Hardcover - Gold" },
  { code: "WC26-MX-GR", label: "Hardcover - Mexico Green" },
  { code: "WC26-US-BL", label: "USA Blue Hardcover - 1 of 2026 - Special Edition" },
  { code: "WC26-CA-RD", label: "Canada Red Hardcover - 1 of 2026 - Special Edition" },
  // Slot for the Mexico Green Special Edition. Included only when a
  // real catalog record for it exists (MEXICO_GREEN_SPECIAL_EDITION_CODE
  // is set). Never invented client-side.
  ...(MEXICO_GREEN_SPECIAL_EDITION_CODE
        ? [{ code: MEXICO_GREEN_SPECIAL_EDITION_CODE,
             label: "Hardcover - Mexico Green - 1 of 2026 - Special Edition" }]
        : []),
  { code: "WC26-LATAM", label: "Hardcover - Colombia" },
  // Display-only rename: keeps the WC26-CH catalog record.
  { code: "WC26-CH",    label: "Hardcover - United Edition" },
  { special: OTHER_OPTION_VALUE, label: "Other" },
];

// ────────────────────────────────────────────────────────────────
// Public helpers
// ────────────────────────────────────────────────────────────────

/**
 * Build the ordered [value, label] tuples the dropdown consumes.
 *
 * @param variants  the array returned by
 *                  `GET /api/catalog/releases/wc26/variants` (`items`)
 * @returns  Array of [value, label] pairs. `value` is either the
 *           variant `id` (when the row maps to a real catalog record)
 *           or the special OTHER_OPTION_VALUE sentinel.
 */
export function buildWcCoverOptions(variants) {
  const byCode = new Map((variants || []).map(v => [v.code, v]));
  const out = [];
  for (const entry of WC_2026_COVER_LABEL_ORDER) {
    if (entry.special === OTHER_OPTION_VALUE) {
      out.push([OTHER_OPTION_VALUE, entry.label]);
      continue;
    }
    const v = byCode.get(entry.code);
    if (v) out.push([v.id, entry.label]);
  }
  return out;
}

/**
 * Resolve the customer-facing label for any variant currently
 * displayed on a collector surface. Used by the dashboard card /
 * album inventory header when the album's `cover_label_override` is
 * NOT set (that override takes absolute precedence upstream).
 *
 * @param variant  a `catalog_album_variants` row (must include `code`
 *                 and `public_display_name`).
 * @returns  the approved customer-facing label if the variant is a
 *           known WC 2026 cover; otherwise the variant's own
 *           `public_display_name` or `name` as a safe fallback.
 */
export function wcCoverLabelForVariant(variant) {
  if (!variant) return "";
  const hit = WC_2026_COVER_LABEL_ORDER.find(
    e => !e.special && e.code === variant.code
  );
  if (hit) return hit.label;
  return variant.public_display_name || variant.name || "";
}

/**
 * Resolve the id of the fallback variant used by the "Other" option.
 * Returns `null` when the fallback catalog row is missing from the
 * variants list (guards against an accidental partial seed).
 */
export function resolveOtherFallbackVariantId(variants) {
  const v = (variants || []).find(x => x.code === OTHER_FALLBACK_VARIANT_CODE);
  return v ? v.id : null;
}
